import App from 'next/app'
import Head from 'next/head'
import { PagesProgressProvider } from '@bprogress/next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Provider } from 'react-redux'
import ThirdPartyScripts from '@/components/ThirdPartyScripts'
import { reduxWrapper } from '@/store'
import { getInitialStoreSetup } from '@/store/helpers/storeHelpers'
import localizationHelpers from '@/store/helpers/localizationHelpers'
import configHelper from '@/store/helpers/configHelpers'
import useBrandClasses from '@/hooks/useBrandClasses'
import { useRootClassSync } from '@/hooks/useRootClassSync'
import { configsActions } from '@/store/configs'
import { useSerwistRegistration } from '@/hooks/useSerwistRegistration'
import * as messageListener from '@/utils/messageListener'
import MainPage from './main'
import '@/styles/custom.scss'

const DEFAULT_TITLE = 'Information for Toyota and Lexus vehicle owners'
const DEFAULT_DESCRIPTION = 'Pub Hub - Information for Toyota and Lexus vehicle owners'

function RootClasses() {
  const classes = useBrandClasses()
  useRootClassSync(classes)
  return null
}

function MyApp(appProps) {
  const router = useRouter()
  const { query } = router
  const { language } = query
  const { store } = reduxWrapper.useWrappedStore(appProps)
  const { Component, pageProps, nonce, gaId } = appProps
  const [loading, setLoading] = useState(true)
  const [iframeDomain, setIFrameDomain] = useState(null)

  useEffect(() => {
    const phFrame = document.getElementById('ph-frame')
    if (phFrame) {
      const height = phFrame.offsetHeight
      window.parent.postMessage(['setHeight', height], '*')
      window.scrollTo(0, 0)
    }
  })

  // This needs to happen right after the initial load.
  useEffect(() => {
    messageListener.init()
    messageListener.subscribe('setDomain', domain => {
      console.log('Message Listener > setDomain useEffect', domain)
      setIFrameDomain(domain)
    })
  }, [])

  useEffect(() => {
    const setApp = async () => {
      try {
        setLoading(true)
        await getInitialStoreSetup({ store, ctx: { query } })
        await localizationHelpers.initialLocaleState(store)(language)

        // Setting height when called from IFrame
        const height = document.getElementById('ph-frame').offsetHeight
        window.parent.postMessage(['setHeight', height], '*')
        // Message listener for iframe communication
        if (iframeDomain) {
          console.log('Setting iframe domain:', iframeDomain)
          configHelper.setIFrameDomain(store)(iframeDomain)
        }

        setLoading(false)
      } catch (error) {
        console.error('Error initializing app:', error)
        setLoading(false)
      }
    }

    setApp()
    // We only care about the initial load, so we can ignore the language change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store])

  useEffect(() => {
    if (store && iframeDomain) {
      console.log('Iframe domain changed:', iframeDomain)
      configHelper.setIFrameDomain(store)(iframeDomain)
    }
  }, [iframeDomain, store])

  useEffect(() => {
    store.dispatch(configsActions.SET_ONLINE_STATUS(navigator.onLine))

    if (!navigator.onLine && router.pathname !== '/cachedPublications' && router.pathname !== '/content') {
      router.replace('/cachedPublications')
    }
  }, [router, store])

  useEffect(() => {
    const goOffline = () => {
      if (router.pathname !== '/cachedPublications' && router.pathname !== '/content') {
        router.replace('/cachedPublications')
      }
    }
    window.addEventListener('offline', goOffline)
    return () => window.removeEventListener('offline', goOffline)
  }, [router])

  const state = store.getState()
  const title = state?.page?.title || DEFAULT_TITLE
  const description = state?.page?.description ? `Pub Hub - ${state.page.description}` : DEFAULT_DESCRIPTION

  useSerwistRegistration()

  return (
    <Provider store={store}>
      <div id="ph-frame">
        <Head>
          <meta httpEquiv="Content-Type" content="text/html charset=UTF-8" />
          <meta content="IE=edge,chrome=1" httpEquiv="X-UA-Compatible" />
          <meta content="width=device-width, initial-scale=1, maximum-scale=1" name="viewport" />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta name="description" content={description} />
          <meta property="og:type" content="website" />
          <title>{title}</title>
        </Head>
        <ThirdPartyScripts nonce={nonce} googleAnalyticsId={gaId} />
        <PagesProgressProvider>
          <MainPage id="ph-main-page" pageProps={pageProps} loading={loading}>
            <RootClasses />
            <Component {...pageProps} />
          </MainPage>
        </PagesProgressProvider>
      </div>
    </Provider>
  )
}

// This is required to get values set in middleware.js file
// that we're only using to generate a nonce for the CSP header
// to execute inline scripts safely.
MyApp.getInitialProps = async appContext => {
  const appProps = await App.getInitialProps(appContext)
  const nonce = appContext.ctx?.req?.headers?.['x-nonce'] ?? ''
  const gaId = appContext.ctx?.req?.headers?.['x-gaid'] ?? ''
  return { ...appProps, nonce, gaId }
}

export default MyApp
